/**
 * Push Notification Service v2 — Frontend (Firebase Cloud Messaging + Web Push)
 *
 * Supports:
 * - Android Chrome / Desktop Chrome: Firebase Cloud Messaging (FCM)
 * - iOS Safari 16.4+ (added to home screen): Standard Web Push via VAPID
 * - Foreground browser notifications when app is in background tab
 * - PWA install prompt capture (beforeinstallprompt)
 * - Service worker version checking & config injection
 *
 * Setup:
 * 1. Tạo project Firebase → Project Settings → General → Web App → Copy config
 * 2. Thêm config vào frontend/public/config.js:
 *    window.APP_CONFIG.firebase = { apiKey: '...', ... }
 *    window.APP_CONFIG.firebaseVapidKey = 'YOUR_VAPID_KEY'
 */

import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import api from './api';

let firebaseApp = null;
let messaging = null;
let _initialized = false;
let _currentToken = null;

/**
 * Get Firebase config from runtime config or env vars
 */
function getFirebaseConfig() {
  // Option 1: Runtime config (recommended for Docker)
  if (window.APP_CONFIG?.firebase) {
    return window.APP_CONFIG.firebase;
  }

  // Option 2: Build-time env vars
  const apiKey = process.env.REACT_APP_FIREBASE_API_KEY;
  if (apiKey) {
    return {
      apiKey,
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
      appId: process.env.REACT_APP_FIREBASE_APP_ID,
    };
  }

  return null;
}

/**
 * Initialize Firebase and messaging
 * @returns {Promise<boolean>}
 */
async function initialize() {
  if (_initialized) return !!messaging;

  try {
    // Check browser support
    const supported = await isSupported();
    if (!supported) {
      console.warn('[Push] Firebase Messaging not supported in this browser');
      _initialized = true;
      return false;
    }

    const config = getFirebaseConfig();
    if (!config) {
      console.warn('[Push] No Firebase config found. Push notifications disabled.');
      console.warn('[Push] Add firebase config to window.APP_CONFIG.firebase in public/config.js');
      _initialized = true;
      return false;
    }

    // Initialize Firebase app
    if (getApps().length === 0) {
      firebaseApp = initializeApp(config);
    } else {
      firebaseApp = getApps()[0];
    }

    messaging = getMessaging(firebaseApp);
    _initialized = true;

    // Send config to service worker and persist to IDB for offline push
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((reg) => {
        reg.active?.postMessage({ type: 'FIREBASE_CONFIG', config });
      }).catch(() => {});
    }

    return true;
  } catch (error) {
    console.warn('[Push] Firebase init error:', error.message);
    _initialized = true;
    return false;
  }
}

/**
 * Request notification permission and get FCM token
 * @returns {Promise<string|null>} FCM token or null
 */
async function requestPermissionAndGetToken() {
  try {
    if (!messaging && !(await initialize())) return null;

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.warn('[Push] Notification permission denied');
      return null;
    }

    // Register service worker if not already registered
    let swRegistration;
    if ('serviceWorker' in navigator) {
      swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      await navigator.serviceWorker.ready;
    }

    const vapidKey = window.APP_CONFIG?.firebaseVapidKey || process.env.REACT_APP_FIREBASE_VAPID_KEY;

    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: swRegistration,
    });

    if (token) {
      console.log('[Push] FCM token obtained');
      _currentToken = token;

      // Register token with backend
      await registerTokenWithBackend(token);

      return token;
    }

    console.warn('[Push] No FCM token available');
    return null;
  } catch (error) {
    console.error('[Push] Error getting FCM token:', error);
    return null;
  }
}

/**
 * Register FCM token with backend
 */
async function registerTokenWithBackend(token) {
  try {
    const platform = detectPlatform();
    const deviceName = detectDeviceName();

    await api.post('/notifications/devices', {
      token,
      platform,
      device_name: deviceName,
    });
    console.log('[Push] Device registered with backend');
  } catch (error) {
    console.error('[Push] Failed to register device:', error.message);
  }
}

/**
 * Unregister current device
 */
async function unregisterDevice() {
  if (!_currentToken) return;
  try {
    await api.delete('/notifications/devices', { data: { token: _currentToken } });
    _currentToken = null;
    console.log('[Push] Device unregistered');
  } catch (error) {
    console.error('[Push] Failed to unregister device:', error.message);
  }
}

/**
 * Set up foreground message handler
 * When the app is open (foreground tab) Firebase doesn't show a native notification.
 * We show one via the Notification API so the user sees it even if the tab is not focused.
 * @param {function} callback - Called with { title, body, data }
 */
function onForegroundMessage(callback) {
  if (!messaging) return () => {};

  return onMessage(messaging, (payload) => {
    const { title, body } = payload.notification || {};
    const data = payload.data || {};

    const msgObj = {
      title: title || 'Thông báo mới',
      body: body || '',
      data,
    };

    // Show native browser notification (visible even when tab is not focused)
    if (Notification.permission === 'granted') {
      try {
        navigator.serviceWorker.ready.then((reg) => {
          reg.showNotification(msgObj.title, {
            body: msgObj.body,
            icon: '/logo192.png',
            badge: '/badge72.png',
            tag: data.notification_id || data.category || 'hr-notif',
            renotify: true,
            data: {
              action_url: data.action_url || '/',
              notification_id: data.notification_id || '',
              category: data.category || 'system',
            },
            actions: [
              { action: 'open', title: 'Xem chi tiết' },
              { action: 'dismiss', title: 'Bỏ qua' },
            ],
          });
        });
      } catch { /* non-fatal */ }
    }

    callback(msgObj);
  });
}

/**
 * Check if push is available and configured
 */
function isAvailable() {
  return !!messaging;
}

/**
 * Check current permission state
 * @returns {'granted'|'denied'|'default'|'unsupported'}
 */
function getPermissionState() {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

/**
 * Get current FCM token (if already obtained)
 */
function getCurrentToken() {
  return _currentToken;
}

/**
 * Detect platform
 */
function detectPlatform() {
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return 'android';
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  if (/Electron/.test(ua)) return 'desktop';
  return 'web';
}

/**
 * Detect device name for display
 */
function detectDeviceName() {
  const ua = navigator.userAgent;
  const match = ua.match(/\(([^)]+)\)/);
  const platform = match ? match[1].split(';')[0].trim() : 'Unknown';
  const browser = ua.match(/(Chrome|Firefox|Safari|Edge|Opera)\/[\d.]+/)?.[0] || 'Browser';
  return `${platform} - ${browser}`;
}

/**
 * Detect if running as installed PWA (added to home screen)
 */
function isInstalledPWA() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

/**
 * Detect if iOS device (iPhone, iPad, iPod)
 */
function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

/**
 * Detect if Android device
 */
function isAndroid() {
  return /android/i.test(navigator.userAgent);
}

/**
 * Check if iOS version supports Web Push (requires iOS 16.4+)
 */
function iOSSupportsPush() {
  if (!isIOS()) return false;
  const match = navigator.userAgent.match(/OS (\d+)_(\d+)/);
  if (!match) return false;
  const major = parseInt(match[1], 10);
  const minor = parseInt(match[2], 10);
  return major > 16 || (major === 16 && minor >= 4);
}

/**
 * PWA install prompt — capture the native event to trigger on demand.
 * Call captureInstallPrompt() once at app startup.
 */
let _installPrompt = null;

function captureInstallPrompt() {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    _installPrompt = e;
    window.dispatchEvent(new CustomEvent('pwa-installable'));
  });

  window.addEventListener('appinstalled', () => {
    _installPrompt = null;
    window.dispatchEvent(new CustomEvent('pwa-installed'));
  });
}

/**
 * Trigger native install prompt (Android/Desktop Chrome/Edge)
 * @returns {Promise<'accepted'|'dismissed'|'unavailable'>}
 */
async function triggerInstallPrompt() {
  if (!_installPrompt) return 'unavailable';
  try {
    _installPrompt.prompt();
    const { outcome } = await _installPrompt.userChoice;
    _installPrompt = null;
    return outcome;
  } catch {
    return 'unavailable';
  }
}

/**
 * Returns install state object
 */
function getInstallState() {
  return {
    isInstalled: isInstalledPWA(),
    canInstall: !!_installPrompt,
    isIOS: isIOS(),
    isAndroid: isAndroid(),
    iOSSupportsPush: iOSSupportsPush(),
    platform: detectPlatform(),
  };
}

/**
 * Send Firebase config to service worker (SW also stores in IDB for offline use)
 */
async function syncConfigToServiceWorker() {
  const config = getFirebaseConfig();
  if (!config || !('serviceWorker' in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    reg.active?.postMessage({ type: 'FIREBASE_CONFIG', config });
  } catch { /* non-fatal */ }
}

const pushService = {
  initialize,
  requestPermissionAndGetToken,
  unregisterDevice,
  onForegroundMessage,
  isAvailable,
  getPermissionState,
  getCurrentToken,
  isInstalledPWA,
  isIOS,
  isAndroid,
  iOSSupportsPush,
  captureInstallPrompt,
  triggerInstallPrompt,
  getInstallState,
  syncConfigToServiceWorker,
};

export default pushService;
